import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { createChatManager } from '../chatManager';
import { buildHiddenContextBlock } from '../utils/chatHelpers';
import { breezeEngineRegistry } from '../../sdk/BreezeEngineRegistry';
function buildContextMessage(prompt, registry) {
  const mergedContext = Object.assign({}, registry.getContext(), prompt.context);
  if (Object.keys(mergedContext).length === 0) return prompt.message;
  return `${buildHiddenContextBlock(mergedContext)}${prompt.message}`;
}
function buildSendOptions(prompt) {
  const {
    turnMetadata,
    messageContexts,
    conversationMetadata,
    fileReferences,
    collectionId,
    hidden
  } = prompt;
  if (!turnMetadata && !(messageContexts !== null && messageContexts !== void 0 && messageContexts.length) && !conversationMetadata && !(fileReferences !== null && fileReferences !== void 0 && fileReferences.length) && !collectionId && !hidden) return undefined;
  return Object.assign({}, turnMetadata && {
    turnMetadata
  }, (messageContexts === null || messageContexts === void 0 ? void 0 : messageContexts.length) && {
    messageContexts
  }, conversationMetadata && {
    conversationMetadata
  }, (fileReferences === null || fileReferences === void 0 ? void 0 : fileReferences.length) && {
    fileReferences
  }, collectionId && {
    collectionId
  }, hidden && {
    hidden
  });
}
export function useChatManager(options) {
  var _registry, _snapshot$active2;
  const optionsRef = useRef(options);
  // eslint-disable-next-line react-hooks/refs
  optionsRef.current = options;
  const managerRef = useRef(null);
  if (managerRef.current === null) {
    // eslint-disable-next-line react-hooks/refs
    managerRef.current = createChatManager(Object.assign({}, options, {
      onError: (cId, error) => {
        var _optionsRef$current$o, _optionsRef$current;
        return (_optionsRef$current$o = (_optionsRef$current = optionsRef.current).onError) === null || _optionsRef$current$o === void 0 ? void 0 : _optionsRef$current$o.call(_optionsRef$current, cId, error);
      },
      onConversationCreated: cId => {
        var _optionsRef$current$o2, _optionsRef$current2;
        return (_optionsRef$current$o2 = (_optionsRef$current2 = optionsRef.current).onConversationCreated) === null || _optionsRef$current$o2 === void 0 ? void 0 : _optionsRef$current$o2.call(_optionsRef$current2, cId);
      },
      onStatusChange: (cId, status, prevStatus, activeTools) => {
        var _optionsRef$current$o3, _optionsRef$current3;
        return (_optionsRef$current$o3 = (_optionsRef$current3 = optionsRef.current).onStatusChange) === null || _optionsRef$current$o3 === void 0 ? void 0 : _optionsRef$current$o3.call(_optionsRef$current3, cId, status, prevStatus, activeTools);
      },
      onStreamEvent: (cId, event) => {
        var _optionsRef$current$o4, _optionsRef$current4;
        return (_optionsRef$current$o4 = (_optionsRef$current4 = optionsRef.current).onStreamEvent) === null || _optionsRef$current$o4 === void 0 ? void 0 : _optionsRef$current$o4.call(_optionsRef$current4, cId, event);
      },
      onTitleChanged: (cId, title) => {
        var _optionsRef$current$o5, _optionsRef$current5;
        return (_optionsRef$current$o5 = (_optionsRef$current5 = optionsRef.current).onTitleChanged) === null || _optionsRef$current$o5 === void 0 ? void 0 : _optionsRef$current$o5.call(_optionsRef$current5, cId, title);
      }
    }));
  }
  const manager = managerRef.current;
  const snapshot = useSyncExternalStore(useCallback(cb => manager.subscribe(cb), [manager]), useCallback(() => manager.getSnapshot(), [manager]));
  const prevAgentIdRef = useRef(options.agentId);
  // eslint-disable-next-line react-hooks/refs
  if (options.agentId !== prevAgentIdRef.current) {
    // eslint-disable-next-line react-hooks/refs
    prevAgentIdRef.current = options.agentId;
    // eslint-disable-next-line react-hooks/refs
    manager.setAgentId(options.agentId);
  }
  const ablyAuthToken = options.mode === 'ably' ? options.getAblyAuthToken : undefined;
  const prevAblyAuthTokenRef = useRef(ablyAuthToken);
  if (ablyAuthToken !== prevAblyAuthTokenRef.current) {
    prevAblyAuthTokenRef.current = ablyAuthToken;
    // eslint-disable-next-line react-hooks/refs
    manager.setAblyAuthToken(ablyAuthToken);
  }
  const registryRef = useRef((_registry = options.__registry) !== null && _registry !== void 0 ? _registry : breezeEngineRegistry);
  const pendingPromptRef = useRef(null);
  const prevActiveStatusRef = useRef(undefined);
  // Prevents re-entrant listener execution: manager.startNew() calls
  // chatManager.notify() which can synchronously trigger React effects (e.g.
  // BreezeEngineChatPage clearing mcpConsentedTools) that call
  // registry.unregisterConversationMetadata() → registry.notify() → this
  // listener again, with the queued prompt still set. Without this guard the
  // re-entrant call sends the message, then the original call parks the same
  // prompt, causing a double-send when streaming completes.
  const isHandlingRegistryEventRef = useRef(false);
  useEffect(() => {
    const registry = registryRef.current;
    const drainRegistryQueue = () => {
      if (isHandlingRegistryEventRef.current) return;
      isHandlingRegistryEventRef.current = true;
      try {
        var _manager$getSnapshot$;
        // Handle openConversation() requests — open the specified conversation
        // without sending a message. Handles the "panel already open" case where
        // BreezeEngineThreadsInit has already run and won't re-execute.
        const requestedConversationId = registry.getRequestedConversationId();
        if (requestedConversationId !== null) {
          const reqInstanceId = registry.getRequestedConversationInstanceId();
          if (reqInstanceId && reqInstanceId !== optionsRef.current.instanceId) {
            return;
          }
          manager.open(requestedConversationId);
          registry.clearRequestedConversationId();
          return;
        }
        if (registry.getShouldStartNewConversation()) {
          const startNewInstanceId = registry.getStartNewConversationInstanceId();
          if (startNewInstanceId && startNewInstanceId !== optionsRef.current.instanceId) {
            return;
          }
          manager.startNew();
          registry.clearShouldStartNewConversation();
          return;
        }
        const prompt = registry.getQueuedPrompt();
        if (!(prompt !== null && prompt !== void 0 && prompt.message)) return;
        if (prompt.instanceId && prompt.instanceId !== optionsRef.current.instanceId) {
          return;
        }
        if (prompt.agentId && prompt.agentId !== optionsRef.current.agentId) {
          pendingPromptRef.current = prompt;
          registry.clearQueuedPrompt();
          return;
        }
        if (prompt.startNewConversation) {
          manager.startNew();
          prevActiveStatusRef.current = undefined;
        }
        const statusAfterStartNew = (_manager$getSnapshot$ = manager.getSnapshot().active) === null || _manager$getSnapshot$ === void 0 ? void 0 : _manager$getSnapshot$.status;
        if (statusAfterStartNew === 'READY') {
          void manager.sendMessage(prompt.hidden ? prompt.message : buildContextMessage(prompt, registryRef.current), buildSendOptions(prompt));
          registry.clearQueuedPrompt();
          pendingPromptRef.current = null;
        } else {
          pendingPromptRef.current = prompt;
          registry.clearQueuedPrompt();
        }
      } finally {
        isHandlingRegistryEventRef.current = false;
      }
    };
    const removeListener = registry.addListener(drainRegistryQueue);
    // Drain any prompt queued before this listener registered. CopilotChannelListener
    // sets isWidgetActive=true before the BZE lazy chunk finishes loading, so the
    // host may call openPanel and fire notify() before this useEffect runs. Without
    // this call the prompt sits in the registry unconsumed and Breeze shows an empty
    // state.
    drainRegistryQueue();

    // Ensures the registry listener is removed even when the iframe is
    // destroyed without React running its cleanup phase (e.g. the parent
    // removes the <iframe> element directly). Without this, the old listener
    // stays in the singleton registry as a zombie and races to consume the
    // next queued prompt before the new widget's listener registers.
    // Skips bfcache navigations (event.persisted === true) so the listener
    // survives back/forward cache restore. Avoids { once: true } so the
    // handler stays registered across multiple bfcache cycles.
    const onPageHide = event => {
      if (!event.persisted) {
        removeListener();
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      removeListener();
    };
    // eslint-disable-next-line react-hooks/refs
  }, [manager]);
  useEffect(() => {
    var _manager$getSnapshot$2;
    const prompt = pendingPromptRef.current;
    if (!(prompt !== null && prompt !== void 0 && prompt.agentId) || prompt.agentId !== options.agentId) return;
    if (prompt.startNewConversation) {
      manager.startNew();
      prevActiveStatusRef.current = undefined;
    }
    if (((_manager$getSnapshot$2 = manager.getSnapshot().active) === null || _manager$getSnapshot$2 === void 0 ? void 0 : _manager$getSnapshot$2.status) === 'READY') {
      pendingPromptRef.current = null;
      void manager.sendMessage(prompt.hidden ? prompt.message : buildContextMessage(prompt, registryRef.current), buildSendOptions(prompt));
      registryRef.current.clearQueuedPrompt();
    }
    // eslint-disable-next-line react-hooks/refs
  }, [manager, options.agentId]);
  useEffect(() => {
    var _snapshot$active;
    const currentStatus = (_snapshot$active = snapshot.active) === null || _snapshot$active === void 0 ? void 0 : _snapshot$active.status;
    const prevStatus = prevActiveStatusRef.current;
    prevActiveStatusRef.current = currentStatus;
    const prompt = pendingPromptRef.current;
    if (currentStatus === 'READY' && prevStatus !== 'READY' && prompt && (!prompt.agentId || prompt.agentId === options.agentId)) {
      pendingPromptRef.current = null;
      if (prompt.startNewConversation) {
        manager.startNew();
      }
      void manager.sendMessage(prompt.hidden ? prompt.message : buildContextMessage(prompt, registryRef.current), buildSendOptions(prompt));
      registryRef.current.clearQueuedPrompt();
    }
    // eslint-disable-next-line react-hooks/refs
  }, [manager, options.agentId, (_snapshot$active2 = snapshot.active) === null || _snapshot$active2 === void 0 ? void 0 : _snapshot$active2.status]);
  const prevToolsRef = useRef(options.tools);
  // eslint-disable-next-line react-hooks/refs
  if (options.tools !== prevToolsRef.current) {
    // eslint-disable-next-line react-hooks/refs
    prevToolsRef.current = options.tools;
    if (options.tools) {
      // eslint-disable-next-line react-hooks/refs
      manager.setTools(options.tools);
    }
  }
  const skillSpecsOption = 'externalSkillSpecs' in options ? options.externalSkillSpecs : undefined;
  const prevSkillSpecsRef = useRef(skillSpecsOption);
  if (skillSpecsOption !== prevSkillSpecsRef.current) {
    prevSkillSpecsRef.current = skillSpecsOption;
    // eslint-disable-next-line react-hooks/refs
    manager.setExternalSkillSpecs(skillSpecsOption !== null && skillSpecsOption !== void 0 ? skillSpecsOption : []);
  }
  const subagentSpecsOption = 'externalSubagentSpecs' in options ? options.externalSubagentSpecs : undefined;
  const prevSubagentSpecsRef = useRef(subagentSpecsOption);
  if (subagentSpecsOption !== prevSubagentSpecsRef.current) {
    prevSubagentSpecsRef.current = subagentSpecsOption;
    // eslint-disable-next-line react-hooks/refs
    manager.setExternalSubagentSpecs(subagentSpecsOption !== null && subagentSpecsOption !== void 0 ? subagentSpecsOption : []);
  }
  const artifactPluginSpecsOption = 'externalArtifactPluginSpecs' in options ? options.externalArtifactPluginSpecs : undefined;
  const prevArtifactPluginSpecsRef = useRef(artifactPluginSpecsOption);
  useEffect(() => {
    if (artifactPluginSpecsOption !== prevArtifactPluginSpecsRef.current) {
      prevArtifactPluginSpecsRef.current = artifactPluginSpecsOption;
      manager.setExternalArtifactPluginSpecs(artifactPluginSpecsOption !== null && artifactPluginSpecsOption !== void 0 ? artifactPluginSpecsOption : []);
    }
    // eslint-disable-next-line react-hooks/refs
  }, [artifactPluginSpecsOption, manager]);
  useEffect(() => {
    return () => {
      manager.destroy();
      managerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/refs
  }, [manager]);

  // eslint-disable-next-line react-hooks/refs
  return useMemo(() => ({
    conversations: snapshot.conversations,
    activeConversationId: snapshot.activeConversationId,
    active: snapshot.active,
    // eslint-disable-next-line react-hooks/refs
    open: manager.open,
    // eslint-disable-next-line react-hooks/refs
    startNew: manager.startNew,
    // eslint-disable-next-line react-hooks/refs
    close: manager.close,
    // eslint-disable-next-line react-hooks/refs
    setActive: manager.setActive,
    // eslint-disable-next-line react-hooks/refs
    sendMessage: manager.sendMessage,
    // eslint-disable-next-line react-hooks/refs
    stop: manager.stop,
    // eslint-disable-next-line react-hooks/refs
    steerTurn: manager.steerTurn,
    // eslint-disable-next-line react-hooks/refs
    clear: manager.clear,
    // eslint-disable-next-line react-hooks/refs
    submitFeedback: manager.submitFeedback,
    // eslint-disable-next-line react-hooks/refs
    addToolResult: manager.addToolResult,
    // eslint-disable-next-line react-hooks/refs
    approveTool: manager.approveTool,
    // eslint-disable-next-line react-hooks/refs
    removeQueuedMessage: manager.removeQueuedMessage,
    // eslint-disable-next-line react-hooks/refs
    resumeTurn: manager.resumeTurn,
    // eslint-disable-next-line react-hooks/refs
    resync: manager.resync,
    // eslint-disable-next-line react-hooks/refs
    clearError: manager.clearError,
    // eslint-disable-next-line react-hooks/refs
    loadMore: manager.loadMore,
    // eslint-disable-next-line react-hooks/refs
    setTools: manager.setTools,
    // eslint-disable-next-line react-hooks/refs
    setExternalSkillSpecs: manager.setExternalSkillSpecs,
    // eslint-disable-next-line react-hooks/refs
    setExternalArtifactPluginSpecs: manager.setExternalArtifactPluginSpecs,
    // eslint-disable-next-line react-hooks/refs
    setExternalSubagentSpecs: manager.setExternalSubagentSpecs,
    // eslint-disable-next-line react-hooks/refs
    injectMessage: manager.injectMessage
  }), [manager, snapshot]);
}